import { useState } from "react";
import { twMerge } from "tailwind-merge";
import type { Article, Category } from "@core/features/support";
import { Type } from "@themes/pollinator/ui/Type";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { Loader } from "@themes/pollinator/ui/Loader";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import {
  SupportSidebar,
  SupportSidebarSearchButton,
  SupportSidebarSupportButton,
} from "../Sidebar";
import {
  SupportCategoryAccordion,
  SupportCategoryAccordionButton,
} from "../CategoryAccordion";

export type Props = {
  articles: Article[];
  categories: Category[];
  isLoading: boolean;
  title?: string;
  description?: string;
  children: React.ReactNode;
  isArticlePage?: boolean;
  onSearchClick: () => void;
  onSupportClick: () => void;
  onNavigationClick: (article: Article) => void;
};

export function SupportPageContent({
  articles,
  categories,
  title,
  description,
  isArticlePage = false,
  isLoading,
  children,
  onSearchClick,
  onSupportClick,
  onNavigationClick,
}: Props) {
  const [isCategoriesPanelOpen, setIsCategoriesPanelOpen] = useState(false);

  return (
    <>
      <AppPageHeader
        title={"Support Center"}
        description={description}
        titleTypeVariant="h3"
        className="px-4 md:px-0 md:max-w-3xl"
        containerClassName="mb-0 md:mb-0"
      />
      <OuterContentContainer
        className="flex flex-1 md:py-0"
        innerClassName="flex-1 flex-col px-0 md:h-full"
      >
        <div className="flex flex-col flex-1 md:flex-row md:items-stretch md:w-full">
          {isLoading || !articles ? (
            <Loader isFullPage />
          ) : (
            <>
              <div
                className={twMerge(
                  "flex flex-col flex-1 gap-4",
                  isArticlePage && "gap-0"
                )}
              >
                <div
                  className={twMerge(
                    "flex flex-col gap-4 py-4 md:py-0",
                    isArticlePage && "flex-col-reverse gap-12 py-0"
                  )}
                >
                  <div className="md:hidden">
                    <div className="flex flex-col gap-2 px-4">
                      <SupportSidebarSearchButton onClick={onSearchClick} />
                      <div className="flex flex-row gap-2 w-full">
                        <SupportCategoryAccordionButton
                          className="flex-[0_0_calc(50%-0.25rem)]"
                          onClick={() =>
                            setIsCategoriesPanelOpen(!isCategoriesPanelOpen)
                          }
                        />
                        <SupportSidebarSupportButton
                          onClick={onSupportClick}
                          className="flex-[0_0_calc(50%-0.25rem)]"
                        />
                      </div>
                    </div>
                    <SupportCategoryAccordion
                      categories={categories}
                      isOpen={isCategoriesPanelOpen}
                      setIsOpen={setIsCategoriesPanelOpen}
                      onNavigationClick={onNavigationClick}
                    />
                  </div>
                </div>
                {title && (
                  <Type as="h3" variant="h2" className="py-8">
                    {title}
                  </Type>
                )}
                {children}
              </div>
              <SupportSidebar
                articles={articles || []}
                categories={categories}
                onSearchClick={onSearchClick}
                onSupportClick={onSupportClick}
                onNavigationClick={onNavigationClick}
              />
            </>
          )}
        </div>
      </OuterContentContainer>
    </>
  );
}

export default SupportPageContent;
